/**
 * @fileoverview Circular wheel component for the Period Calculator.
 * Handles wheel rendering, drag interactions, and day selection.
 * @module period-tracker/period-wheel
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.wheel
 * @description Manages the circular wheel view rendering and interactions.
 */
femcare.periodTracker.wheel = {
    container: null,
    dayNumbers: null,
    phaseRing: null,
    separatorRing: null,
    dayIndicatorRing: null,
    centerImageContainer: null,
    centerImages: null,
    currentRotation: 0,
    isDragging: false,
    startAngle: 0,
    velocity: 0,
    lastTime: 0,
    currentDay: 1,
    totalDaysInMonth: 30,
    dayPhases: {},
    phaseLabels: null,
    onDayChange: null,
    debouncedInfoUpdate: null,

    /**
     * Initializes the wheel module.
     * @param {Object} config - Configuration object
     * @param {HTMLElement} config.container - Wheel container
     * @param {HTMLElement} config.dayNumbers - Day numbers element
     * @param {HTMLElement} config.phaseRing - Phase ring element
     * @param {HTMLElement} config.separatorRing - Separator ring element
     * @param {HTMLElement} config.dayIndicatorRing - Day indicator ring
     * @param {HTMLElement} config.centerImageContainer - Center image container
     * @param {NodeList} config.centerImages - Center images
     * @param {Object} config.phaseLabels - Phase label strings
     * @param {Function} config.onDayChange - Day change callback
     */
    init: function (config) {
        this.container = config.container;
        this.dayNumbers = config.dayNumbers;
        this.phaseRing = config.phaseRing;
        this.separatorRing = config.separatorRing;
        this.dayIndicatorRing = config.dayIndicatorRing;
        this.centerImageContainer = config.centerImageContainer;
        this.centerImages = config.centerImages;
        this.phaseLabels = config.phaseLabels;
        this.onDayChange = config.onDayChange;

        this.debouncedInfoUpdate = this.debounce(() => {
            if (this.onDayChange) {
                this.onDayChange(this.currentDay);
            }
        }, 50);

        this.bindEvents();
    },

    /**
     * Renders the wheel for a given month.
     * @param {Date} month - Month to render
     * @param {Object} phaseData - Phase data from cycleLogic
     */
    render: function (month, phaseData) {
        this.totalDaysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        this.dayPhases = phaseData.dayPhases;

        this.createDayNumbers();
        this.createPhaseRing();
        this.createMonthSeparators();
        this.createDayIndicators();
    },

    /**
     * Sets the current day and updates display.
     * @param {number} day - Day to set
     * @param {boolean} animate - Whether to animate
     */
    setCurrentDay: function (day, animate) {
        this.currentDay = day;
        const rotationStep = 360 / this.totalDaysInMonth;
        const targetAngle = -((day - 1) * rotationStep);

        if (animate) {
            this.animateToAngle(targetAngle);
        } else {
            this.rotateTo(targetAngle);
        }

        this.updateDayIndicators(day);
        this.updateDayNumbers(day);
        this.updateCenterImageByPhase(day);
    },

    /**
     * Creates day number elements.
     */
    createDayNumbers: function () {
        this.dayNumbers.innerHTML = '';
        const rotationStep = 360 / this.totalDaysInMonth;
        const cycleLogic = femcare.periodTracker.cycleLogic;

        for (let dayNumber = 1; dayNumber <= this.totalDaysInMonth; dayNumber++) {
            const dayElement = document.createElement('div');
            dayElement.className = 'day-number';
            dayElement.textContent = dayNumber;
            dayElement.dataset.day = dayNumber;

            const phase = cycleLogic.getPhaseForDay(dayNumber, this.dayPhases);
            if (phase) {
                dayElement.classList.add(`phase-${phase.className}`);
                dayElement.style.backgroundColor = phase.color;
            }

            const angle = (dayNumber - 1) * rotationStep - 90;
            const radius = 170;
            const x = 50 + (radius * Math.cos(angle * Math.PI / 180)) / 350 * 100;
            const y = 50 + (radius * Math.sin(angle * Math.PI / 180)) / 350 * 100;

            dayElement.style.left = `${x}%`;
            dayElement.style.top = `${y}%`;
            dayElement.style.transform = `translate(-50%, -50%) rotate(${-angle}deg)`;

            this.dayNumbers.appendChild(dayElement);
        }
    },

    /**
     * Creates the phase ring with colored segments.
     */
    createPhaseRing: function () {
        if (!this.phaseRing) return;

        this.phaseRing.innerHTML = '';
        const phaseColors = femcare.periodTracker.config.phaseRingColors;
        const phaseSegments = this.calculatePhaseSegments();
        const rotationStep = 360 / this.totalDaysInMonth;

        phaseSegments.forEach((segment) => {
            const phaseElement = document.createElement('div');
            phaseElement.className = `phase phase-${segment.phase}`;

            const startAngle = -90 + (segment.startDay - 1) * rotationStep;
            const endAngle = -90 + segment.endDay * rotationStep;
            const sweepAngle = endAngle - startAngle;
            const largeArcFlag = sweepAngle > 180 ? 1 : 0;

            const outerRadius = 50;
            const innerRadius = 44.3;

            const svgPathData = this.createArcPath(startAngle, endAngle, outerRadius, innerRadius, largeArcFlag);
            let phaseLabelSvg = '';

            if (sweepAngle > 30 && this.phaseLabels) {
                phaseLabelSvg = this.createPhaseText(segment, startAngle, endAngle, largeArcFlag);
            }

            phaseElement.innerHTML = `
                <svg viewBox="0 0 100 100" class="phase-svg">
                    <path d="${svgPathData}" fill="${phaseColors[segment.phase]}" opacity="1" />
                    ${phaseLabelSvg}
                </svg>
            `;

            this.phaseRing.appendChild(phaseElement);
        });
    },

    /**
     * Calculates phase segments for the month.
     * @returns {Array} Array of segment objects
     */
    calculatePhaseSegments: function () {
        const segments = [];
        let currentPhase = null;
        let segmentStart = null;
        let segmentEnd = null;

        for (let day = 1; day <= this.totalDaysInMonth; day++) {
            const dayPhase = this.dayPhases[day] || 'follicular';

            if (currentPhase !== dayPhase) {
                if (currentPhase !== null) {
                    segments.push({ phase: currentPhase, startDay: segmentStart, endDay: segmentEnd });
                }
                currentPhase = dayPhase;
                segmentStart = day;
            }
            segmentEnd = day;
        }

        if (currentPhase !== null) {
            segments.push({ phase: currentPhase, startDay: segmentStart, endDay: segmentEnd });
        }

        return segments;
    },

    /**
     * Creates SVG arc path data.
     */
    createArcPath: function (startAngle, endAngle, outerRadius, innerRadius, largeArcFlag) {
        const startXOuter = 50 + outerRadius * Math.cos(startAngle * Math.PI / 180);
        const startYOuter = 50 + outerRadius * Math.sin(startAngle * Math.PI / 180);
        const endXOuter = 50 + outerRadius * Math.cos(endAngle * Math.PI / 180);
        const endYOuter = 50 + outerRadius * Math.sin(endAngle * Math.PI / 180);
        const startXInner = 50 + innerRadius * Math.cos(startAngle * Math.PI / 180);
        const startYInner = 50 + innerRadius * Math.sin(startAngle * Math.PI / 180);
        const endXInner = 50 + innerRadius * Math.cos(endAngle * Math.PI / 180);
        const endYInner = 50 + innerRadius * Math.sin(endAngle * Math.PI / 180);

        return [
            `M ${startXOuter} ${startYOuter}`,
            `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endXOuter} ${endYOuter}`,
            `L ${endXInner} ${endYInner}`,
            `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${startXInner} ${startYInner}`,
            'Z'
        ].join(' ');
    },

    /**
     * Creates phase text element for SVG.
     */
    createPhaseText: function (segment, startAngle, endAngle, largeArcFlag) {
        const textRadius = 47.15 - 2;
        const textStartX = 50 + textRadius * Math.cos(startAngle * Math.PI / 180);
        const textStartY = 50 + textRadius * Math.sin(startAngle * Math.PI / 180);
        const textEndX = 50 + textRadius * Math.cos(endAngle * Math.PI / 180);
        const textEndY = 50 + textRadius * Math.sin(endAngle * Math.PI / 180);

        const textPathData = `M ${textStartX} ${textStartY} A ${textRadius} ${textRadius} 0 ${largeArcFlag} 1 ${textEndX} ${textEndY}`;
        const curveId = `text-curve-${segment.phase}-${segment.startDay}`;

        return `
            <defs><path id="${curveId}" d="${textPathData}" /></defs>
            <text font-size="4" fill="#333" class="phase-text">
                <textPath href="#${curveId}" startOffset="50%" text-anchor="middle">
                    ${this.phaseLabels[segment.phase]}
                </textPath>
            </text>
        `;
    },

    /**
     * Creates month separator line.
     */
    createMonthSeparators: function () {
        if (!this.separatorRing) return;

        this.separatorRing.innerHTML = '';
        const rotationStep = 360 / this.totalDaysInMonth;
        const separatorAngle = -90 - (rotationStep / 2);
        const angleRad = separatorAngle * Math.PI / 180;

        const innerRadius = 42;
        const outerRadius = 53;
        const x1 = 50 + innerRadius * Math.cos(angleRad);
        const y1 = 50 + innerRadius * Math.sin(angleRad);
        const x2 = 50 + outerRadius * Math.cos(angleRad);
        const y2 = 50 + outerRadius * Math.sin(angleRad);

        const separatorElement = document.createElement('div');
        separatorElement.className = 'month-separator';
        separatorElement.innerHTML = `
            <svg viewBox="0 0 100 100" class="separator-svg">
                <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#2F2F2F" stroke-width="0.3" />
            </svg>
        `;

        this.separatorRing.appendChild(separatorElement);
    },

    /**
     * Creates day indicator elements.
     */
    createDayIndicators: function () {
        if (!this.dayIndicatorRing) return;

        this.dayIndicatorRing.innerHTML = '';
        const rotationStep = 360 / this.totalDaysInMonth;

        for (let dayNumber = 1; dayNumber <= this.totalDaysInMonth; dayNumber++) {
            const indicatorElement = document.createElement('div');
            indicatorElement.className = 'day-indicator';
            indicatorElement.dataset.day = dayNumber;

            const angle = (dayNumber - 1) * rotationStep - 90;
            const radius = 100;
            const x = 50 + (radius * Math.cos(angle * Math.PI / 180)) / 200 * 100;
            const y = 50 + (radius * Math.sin(angle * Math.PI / 180)) / 200 * 100;

            indicatorElement.style.left = `${x}%`;
            indicatorElement.style.top = `${y}%`;

            this.dayIndicatorRing.appendChild(indicatorElement);
        }
    },

    /**
     * Updates day indicators.
     * @param {number} chosenDay - Day to highlight
     */
    updateDayIndicators: function (chosenDay) {
        if (!this.dayIndicatorRing) return;

        const indicators = this.dayIndicatorRing.querySelectorAll('.day-indicator');
        indicators.forEach((indicator, index) => {
            indicator.classList.toggle('day-indicator--chosen', index + 1 === chosenDay);
        });
    },

    /**
     * Updates day numbers.
     * @param {number} activeDay - Day to mark active
     */
    updateDayNumbers: function (activeDay) {
        if (!this.dayNumbers) return;

        const dayElements = this.dayNumbers.querySelectorAll('.day-number');
        dayElements.forEach((dayElement) => {
            const dayNum = parseInt(dayElement.dataset.day, 10);
            dayElement.classList.toggle('day-number--active', dayNum === activeDay);
        });
    },

    /**
     * Updates center image based on phase.
     * @param {number} day - Day number
     */
    updateCenterImageByPhase: function (day) {
        if (!day || isNaN(day) || day < 1 || day > this.totalDaysInMonth) {
            day = 1;
        }

        const phase = this.dayPhases[day];
        if (!phase) return;

        if (this.centerImageContainer) {
            this.centerImageContainer.classList.remove(
                'center-image--menstruation',
                'center-image--follicular',
                'center-image--ovulation',
                'center-image--luteal'
            );
            this.centerImageContainer.classList.add(`center-image--${phase}`);
        }

        if (this.centerImages) {
            this.centerImages.forEach(img => {
                const imgPhase = img.getAttribute('data-cycle');
                img.classList.toggle('hidden', imgPhase !== phase);
            });
        }
    },

    /**
     * Rotates wheel to angle.
     * @param {number} angle - Target angle
     * @param {boolean} skipCallback - Skip day change callback
     */
    rotateTo: function (angle, skipCallback) {
        this.currentRotation = angle;

        this.dayNumbers.style.transform = `rotate(${angle}deg)`;
        if (this.phaseRing) {
            this.phaseRing.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
        }
        if (this.separatorRing) {
            this.separatorRing.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
        }
        if (this.dayIndicatorRing) {
            this.dayIndicatorRing.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
        }

        const dayElements = this.dayNumbers.querySelectorAll('.day-number');
        dayElements.forEach((dayElement) => {
            dayElement.style.transform = `translate(-50%, -50%) rotate(${-angle}deg)`;
        });

        const rotationStep = 360 / this.totalDaysInMonth;
        const normalizedAngle = ((angle % 360) + 360) % 360;
        const dayOffset = Math.round(normalizedAngle / rotationStep);
        let newDay = 1 - dayOffset;

        if (newDay <= 0) newDay += this.totalDaysInMonth;
        if (newDay > this.totalDaysInMonth) newDay -= this.totalDaysInMonth;
        if (isNaN(newDay) || newDay < 1 || newDay > this.totalDaysInMonth) newDay = 1;

        if (newDay !== this.currentDay) {
            this.currentDay = newDay;
            this.updateDayIndicators(newDay);
            this.updateDayNumbers(newDay);

            if (!skipCallback) {
                this.updateCenterImageByPhase(newDay);
                if (this.isDragging) {
                    this.debouncedInfoUpdate();
                } else if (this.onDayChange) {
                    this.onDayChange(newDay);
                }
            }
        }
    },

    /**
     * Snaps to nearest day.
     */
    snapToNearestDay: function () {
        const rotationStep = 360 / this.totalDaysInMonth;
        const nearestDayIndex = Math.round(this.currentRotation / rotationStep);
        this.animateToAngle(nearestDayIndex * rotationStep);
    },

    /**
     * Animates to target angle.
     * @param {number} targetAngle - Target angle
     */
    animateToAngle: function (targetAngle) {
        const startAngle = this.currentRotation;
        let deltaAngle = targetAngle - startAngle;

        if (deltaAngle > 180) deltaAngle -= 360;
        if (deltaAngle < -180) deltaAngle += 360;

        const duration = Math.max(200, Math.min(800, Math.abs(deltaAngle) * 2));
        let startTime = null;

        const animate = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 2);

            const skipCallback = progress < 1;
            this.rotateTo(startAngle + deltaAngle * easeOut, skipCallback);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    },

    /**
     * Gets angle from position.
     */
    getAngleFromPosition: function (clientX, clientY) {
        const rect = this.container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        return Math.atan2(clientY - centerY, clientX - centerX) * 180 / Math.PI;
    },

    /**
     * Handles drag start.
     */
    handleStart: function (event) {
        const target = event.target;
        if (target.closest('.center-image') || target.closest('.v-image') || target.closest('.circular-period-calendar__info')) {
            return;
        }

        event.preventDefault();
        this.isDragging = true;

        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;

        this.startAngle = this.getAngleFromPosition(clientX, clientY);
        this.lastTime = Date.now();
        this.velocity = 0;
    },

    /**
     * Handles drag move.
     */
    handleMove: function (event) {
        if (!this.isDragging) return;
        event.preventDefault();

        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;

        const currentAngle = this.getAngleFromPosition(clientX, clientY);
        let deltaAngle = currentAngle - this.startAngle;

        if (deltaAngle > 180) deltaAngle -= 360;
        if (deltaAngle < -180) deltaAngle += 360;

        const now = Date.now();
        const deltaTime = now - this.lastTime;

        if (deltaTime > 0) {
            const instantVelocity = deltaAngle / deltaTime;
            this.velocity = this.velocity * 0.8 + instantVelocity * 0.2;
        }

        this.rotateTo(this.currentRotation + deltaAngle);
        this.startAngle = currentAngle;
        this.lastTime = now;
    },

    /**
     * Handles drag end.
     */
    handleEnd: function (event) {
        if (!this.isDragging) return;
        event.preventDefault();

        this.isDragging = false;
        document.body.style.userSelect = '';
        document.body.style.overflow = '';

        this.updateCenterImageByPhase(this.currentDay);
        if (this.onDayChange) {
            this.onDayChange(this.currentDay);
        }

        if (Math.abs(this.velocity) > 0.05) {
            const momentum = this.velocity * 100;
            const targetAngle = this.currentRotation + momentum;
            const rotationStep = 360 / this.totalDaysInMonth;
            const nearestDayIndex = Math.round(targetAngle / rotationStep);
            this.animateToAngle(nearestDayIndex * rotationStep);
        } else {
            this.snapToNearestDay();
        }
    },

    /**
     * Binds event listeners.
     */
    bindEvents: function () {
        this.dayNumbers.addEventListener('mousedown', this.handleStart.bind(this));
        document.addEventListener('mousemove', this.handleMove.bind(this));
        document.addEventListener('mouseup', this.handleEnd.bind(this));

        this.dayNumbers.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.handleEnd.bind(this), { passive: false });

        this.dayNumbers.addEventListener('contextmenu', (event) => event.preventDefault());
    },

    /**
     * Debounce utility.
     */
    debounce: function (func, wait) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    },

    /**
     * Gets current day.
     * @returns {number} Current day
     */
    getCurrentDay: function () {
        return this.currentDay;
    }
};
